import React, { useCallback, useEffect, useState, useRef } from "react";
import { TextField, Autocomplete, Typography, Chip } from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import debounce from "lodash/debounce";
import { searchableSelectCheck } from "./searchable-select-check";
import { toEntityTitleSubtitle } from "../common/string-helpers";

/**
 * {value: string, enums: Array<string|object>}
 */
const SearchableSelectInput = ({
  valueSpec,
  onChange,
  onError,
  multiSelect = false,
  fetchValueSpec,
  listPosition = "auto",
  style = "standard",
  zIndex = 100,
}) => {
  const [open, setOpen] = useState(false);
  const [specData, setSpecData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState("");
  const [selectedValue, setSelectedValue] = useState(multiSelect ? [] : "");
  const [error, setError] = useState(false);
  const titleBreak = useRef(true);
  const isReadOnly = valueSpec.readOnly;

  useEffect(() => {
    if (valueSpec) {
      console.log("valueSpec changed", valueSpec);
      setLoading(true);
      // Find the corresponding object from enums if value is a string
      let initialValue = valueSpec.value;
      if (valueSpec.titleBreak) {
        initialValue = toEntityTitleSubtitle(
          valueSpec.value,
          valueSpec.titleBreak
        );
      }
      setSelectedValue(initialValue);
      setSpecData(valueSpec);
      titleBreak.current = valueSpec.titleBreak || false;
      refreshValueSpec(inputText);
      const err = checkValue(valueSpec.value, valueSpec);
      if (err) {
        setError(true);
        onError();
      }
    }
    return () => {};
  }, [valueSpec]);

  const debouncedRefreshValueSpec = useCallback(
    debounce((currentInput) => {
      refreshValueSpec(currentInput);
    }, 1000),
    []
  );

  const handleInputChange = (event, newValue) => {
    console.debug("handleInputChange", newValue);
    if (isReadOnly) return;
    setInputText(newValue);
    if (fetchValueSpec) {
      debouncedRefreshValueSpec(newValue);
    }
  };

  const refreshValueSpec = (inputText) => {
    setLoading(true);
    if (fetchValueSpec) {
      fetchValueSpec(inputText)
        .then((vs) => {
          const input = vs.value;
          if (input === inputText) {
            // Convert enums to objects if they're strings
            if (Array.isArray(vs.valueSpec.enums)) {
              vs.valueSpec.enums = vs.valueSpec.enums.map((e) => {
                return toEntityTitleSubtitle(e, titleBreak.current);
              });
            }
            console.log("vs.valueSpec", vs.valueSpec);
            setSpecData(vs.valueSpec);
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching options:", error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const handleSelectChange = (event, newValue) => {
    if (isReadOnly) return;
    if (newValue === null) return;
    console.log("handleSelectChange", newValue);
    console.log("specData", specData);
    const err = checkValue(newValue, specData);
    setSelectedValue(newValue);
    setError(err);
    if (err) {
      onError();
    } else {
      if (newValue && typeof newValue === "object" && newValue.value) {
        onChange(newValue.value);
      } else {
        onChange(newValue);
      }
    }
  };

  const handleBlur = (event, newVal) => {
    if (isReadOnly) return;

    // Check if the input text matches any option
    const isValidOption = specData?.enums?.some((option) => {
      if (typeof option === "object") {
        return option.title === inputText || option.value === inputText;
      }
      return option === inputText;
    });

    if (!isValidOption) {
      setError(true);
      onError();
    }
  };

  const checkValue = (value, spec) => {
    if (multiSelect) {
      for (let i = 0; i < value?.length; i++) {
        const val = typeof value[i] === "object" ? value[i].value : value[i];
        const err = !searchableSelectCheck(val, spec);
        if (err) {
          return err;
        }
      }
      return false;
    } else {
      console.debug("checkValue", value, spec);
      const val = typeof value === "object" ? value.value : value;
      const err = !searchableSelectCheck(val, spec);
      console.debug(val, err, spec);
      return err;
    }
  };

  const getOptionLabel = (option) => {
    if (typeof option === "object" && option.title) {
      return option.title;
    }
    return option;
  };

  const renderOption = (props, option) => {
    const compactMenuItemStyles = {
      minHeight: "24px",
      padding: "4px 8px",
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 16,
        right: 16,
        height: "1px",
        backgroundColor: "hsl(var(--border) / 0.08)",
      },
      "&:last-child::after": {
        display: "none",
      },
    };

    const standardMenuItemStyles = {
      minHeight: "auto",
      padding: "6px 16px",
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 16,
        right: 16,
        height: "1px",
        backgroundColor: "hsl(var(--border) / 0.08)",
      },
      "&:last-child::after": {
        display: "none",
      },
    };

    if (typeof option === "object" && option.value) {
      return (
        <li
          {...props}
          key={`${option.title}-${option.value}`}
          style={
            style === "compact" ? compactMenuItemStyles : standardMenuItemStyles
          }
        >
          <div className="flex flex-col">
            <Typography className="text-base">
              {option.title || option.value}
            </Typography>
            {option.subtitle && (
              <Typography className="text-sm text-secondary">
                {option.subtitle}
              </Typography>
            )}
          </div>
        </li>
      );
    }
    return (
      <li
        {...props}
        key={option}
        style={
          style === "compact" ? compactMenuItemStyles : standardMenuItemStyles
        }
      >
        {option}
      </li>
    );
  };

  return (
    <div
      className="flex flex-col w-full h-full overflow-visible"
      style={{
        opacity: isReadOnly ? 0.7 : 1,
        zIndex,
      }}
    >
      {valueSpec && (
        <div
          className="flex flex-col h-full"
          style={{
            gap: style === "compact" ? "8px" : "16px",
            zIndex,
          }}
        >
          <div className="flex-1 min-h-0 relative" style={{ zIndex }}>
            <Autocomplete
              multiple={multiSelect ?? false}
              open={isReadOnly ? false : open}
              onOpen={() => !isReadOnly && setOpen(true)}
              onClose={() => setOpen(false)}
              options={Array.isArray(specData?.enums) ? specData.enums : []}
              loading={loading}
              error={error}
              iscompact={style === "compact" ? "true" : "false"}
              inputValue={
                !open
                  ? selectedValue &&
                    typeof selectedValue === "object" &&
                    selectedValue.title
                    ? selectedValue.title
                    : inputText
                  : inputText
              }
              onInputChange={handleInputChange}
              onChange={handleSelectChange}
              onBlur={handleBlur}
              value={selectedValue ?? (multiSelect ? [] : null)}
              readOnly={isReadOnly}
              disableClearable={isReadOnly}
              getOptionLabel={getOptionLabel}
              renderOption={renderOption}
              filterOptions={(options, { inputValue }) => {
                const searchWords = inputValue
                  .toLowerCase()
                  .split(/\s+/)
                  .filter(Boolean);
                return options.filter((option) => {
                  if (typeof option === "object") {
                    const title = option.title?.toLowerCase() || "";
                    const subtitle = option.subtitle?.toLowerCase() || "";
                    const combinedText = `${title} ${subtitle}`;
                    return searchWords.every((word) =>
                      combinedText.includes(word)
                    );
                  }
                  const optionText = option.toLowerCase();
                  return searchWords.every((word) => optionText.includes(word));
                });
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === "object" && typeof value === "object") {
                  return option.value === value.value;
                }
                if (typeof option === "object") {
                  return option.value === value;
                }
                if (typeof value === "object") {
                  return option === value.value;
                }
                return option === value;
              }}
              componentsProps={{
                popper: {
                  placement:
                    listPosition === "top" ? "top-start" : "bottom-start",
                  style: { zIndex },
                  modifiers: [
                    {
                      name: "sameWidth",
                      enabled: true,
                      phase: "beforeWrite",
                      requires: ["computeStyles"],
                      fn: ({ state }) => {
                        state.styles.popper.width = `${state.rects.reference.width}px`;
                      },
                      effect: ({ state }) => {
                        state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                      },
                    },
                  ],
                },
                paper: {
                  elevation: 3,
                  sx: {
                    mt: 1,
                    overflow: "hidden",
                    borderRadius: 2,
                    boxShadow: `0 4px 20px ${alpha(
                      "hsl(var(--foreground))",
                      0.1
                    )}`,
                    zIndex,
                  },
                },
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  const label =
                    typeof option === "object" ? option.title : option;
                  return (
                    <Chip
                      {...tagProps}
                      key={key}
                      label={label}
                      onDelete={isReadOnly ? undefined : tagProps.onDelete}
                      className="h-6 rounded-lg text-xs"
                      style={{
                        opacity: isReadOnly ? 0.7 : 1,
                        zIndex,
                        backgroundColor: "hsl(var(--primary) / 0.1)",
                        color: "hsl(var(--primary))",
                        padding: "0 8px",
                      }}
                    />
                  );
                })
              }
              className={isReadOnly ? "opacity-70" : ""}
              style={{
                zIndex,
                ...(isReadOnly && {
                  pointerEvents: "none",
                }),
              }}
              noOptionsText={loading ? "Loading..." : "No options"}
              loadingText={
                <div className="flex justify-center p-4">
                  <CircularProgress size={20} />
                </div>
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select value"
                  variant="outlined"
                  error={error}
                  className="h-full"
                  style={{
                    zIndex,
                    height: "100%",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    readOnly: isReadOnly,
                    style: {
                      ...params.InputProps?.style,
                      height: "100%",
                      alignItems: "center",
                      padding:
                        style === "compact"
                          ? "6px 40px 6px 10px"
                          : "8px 40px 8px 14px",
                      minHeight: style === "compact" ? "40px" : "48px",
                    },
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                    placeholder: selectedValue
                      ? ""
                      : multiSelect
                      ? "Select values"
                      : "Select a value",
                  }}
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelectInput;
